import React, { useEffect } from "react";
import { Input } from "reactstrap";

const InputAmount = ({
  input,
  placeholder,
  className,
  pattern,
  disabled,
  onKeyUp,
  amounNewValue,
  reseter,
  min,
  max,
  mode, // To distinguish between profile and symptom input
  onSemiColonTyped,
  HHMM_Allowed,
}) => {

  // Reset to amounNewValue when reseter toggled or when changes are made
  useEffect(() => {
    if (input && typeof input.onChange === "function") {
      input.onChange(amounNewValue || "");
    }
  }, [reseter, amounNewValue]);

  // This regex validates a number-like string that:
  // - Starts with a digit
  // - May contain only one of each: '/', '.', '&', or space
  // - Disallows two or more of those characters in a row
  // - Prevents mixed usage of '.' with '/', '&', or space
  // - Ensures no '&' comes after or before a space
  // - Allows formats like "1/2", "2.5", "3 & 1", "4 1/2", but not "1//2", "2.5/1", "1 & 2", or "1 2&3"
  const fractionRegex = /^(?!.*[\/.& ]{2,})(?!.*([\/.& ])(?=.*\1))(?!.*\..*[\/& ])(?!.*[\/& ].*\.)((?!.*&.* ).*)(?!.* .*&)^([0-9][0-9\/.& ]*)$/;

  // Matches a numeric string that:
  // - Starts with a digit
  // - May contain only one optional decimal point
  // - Allows digits after the decimal (or none)
  // - Disallows strings starting with '.' or having multiple dots
  const decimalOnlyRegex = /^[0-9]+(?:\.[0-9]*)?$/;

  const semicolonPattern = /^\d{0,2};?\d{0,2}$/;

  // Block semicolon entry when HHMM_Allowed is false
  const handleKeyDown = (e) => {
    if (!HHMM_Allowed && (e.key === ";" || e.key === ":")) {
      e.preventDefault(); // Block colon and semicolon input
    }
  };

  // Block paste if the value include semicolon but HHMM_Allowed is false
  const handlePaste = (e) => {
    const pastedText = (e.clipboardData || window.clipboardData).getData("text");
    if (!HHMM_Allowed && (pastedText.includes(";") || pastedText.includes(":"))) {
      e.preventDefault(); // Block the paste
    }
  };

  const handleInput = (e) => {
    const val = e.target.value;
    const hasSemiColon = val.includes(";");

    // ✅ Allow semicolon-based input only if:
    // - Category is 03 or 07
    // - Format is strictly "0-2 digits ; 2 digits"
    const semicolonValid = semicolonPattern.test(val);

    // ✅ If semicolon is detected and valid for category, trigger switch
    if (hasSemiColon && HHMM_Allowed && semicolonValid) {
      // ⏱ Show semicolon in field first
      if (input && typeof input.onChange === "function") {
        input.onChange(val);
      }

      // 🔁 Trigger switch to InputHHMM
      if (typeof onSemiColonTyped === "function") {
        onSemiColonTyped(val); // triggers switch to InputHHMM
      }

      return;// exit here, don't call input.onChange from this component
    }

    // ✅ Allow regular formats
    const regexToUse = mode === "allowfraction" ? fractionRegex : decimalOnlyRegex;

    if ((val === "" || regexToUse.test(val)) && input && typeof input.onChange === "function") {
      input.onChange(val);
    }
  };

  // add these above the return
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragOver = (e) => {
    // Without this, some browsers still allow dropping
    e.preventDefault();
  };

  const handleBeforeInput = (e) => {
    // Belt & suspenders: blocks programmatic “insertFromDrop”
    if (e && e.nativeEvent && e.nativeEvent.inputType === "insertFromDrop") {
      e.preventDefault();
    }
  };

  return (
    <Input
      autoComplete="off"
      {...input}
      placeholder={placeholder}
      onChange={handleInput}
      value={input.value || ""}
      className={className}
      pattern={pattern}
      disabled={disabled ? "disabled" : false}
      onKeyUp={onKeyUp}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste} 
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onBeforeInput={handleBeforeInput}
      min={min}
      max={max}
      maxLength="5"
    />
  );
};

export default InputAmount;



// WEBPACK FOOTER //
// ./src/components/InputAmount.js